import { useState } from "react";
import styled from "styled-components";
import { LiaTimesSolid } from "react-icons/lia";
import { useUploadFile } from "../../hook";
import { useAtom } from "jotai";
import { adPayloadAtom } from "../../atom/digitalAdPayloadAtom";

const FileUploader = ({
  label,
  accept,
  handleFileUpload,
  uploadFileType,
  allowedDimensions = [], // Array of allowed dimensions (e.g., ["192x192", "900x600"])
}) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [payload, setPayload] = useAtom(adPayloadAtom);

  const maxSize = 30 * 1024 * 1024; // 30MB

  const { uploadFile, files, uploading } = useUploadFile();

  const onFileChange = (event) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    if (!accept.includes(selectedFile.type)) {
      setError("Invalid file type. Please upload a valid file.");
      return;
    }

    if (selectedFile.size > maxSize) {
      setError("File size exceeds the 30MB limit.");
      return;
    }

    // Validate dimensions if it's an image
    if (selectedFile.type.startsWith("image/")) {
      const img = new Image();
      img.onload = function () {
        const imgDimensions = `${this.width}x${this.height}`;
        if (
          allowedDimensions.length &&
          !allowedDimensions.includes(imgDimensions)
        ) {
          setError(
            `Invalid dimensions. Allowed dimensions: ${allowedDimensions.join(
              ", "
            )}`
          );
          return;
        }
        setFile(selectedFile);
        setError("");
        // handleFileUpload(selectedFile);
      };
      img.src = URL.createObjectURL(selectedFile);
    } else {
      setFile(selectedFile);
      setError("");
      return;
      //   handleFileUpload(selectedFile);
    }
    uploadFile(uploadFileType, selectedFile);

    if (uploadFileType === "iconFile") {
      setPayload((prev) => ({ ...prev, iconFilename: files.iconFile }));
    }
    if (uploadFileType === "imageFile") {
      setPayload((prev) => ({ ...prev, imageFilename: files.imageFile }));
    }
    if (uploadFileType === "videoFile") {
      setPayload((prev) => ({ ...prev, videoFilename: files.videoFile }));
    }
  };

  const removeFile = () => {
    setFile(null);
    setError("");
  };


  return (
    <Container>
      <Label>{label}</Label>

      {/* Allowed Dimensions Info */}
      {allowedDimensions.length > 0 && (
        <DimensionInfo>
          Allowed Dimensions: {allowedDimensions.join(", ")}
        </DimensionInfo>
      )}

      {/* Drag-and-Drop Zone */}
      <UploadBox htmlFor={label}>
        <p>
          Drag & drop your file here, or <span>click to upload</span>.
        </p>
        <input type="file" id={label} accept={accept} onChange={onFileChange} />
      </UploadBox>

      {/* Display selected file */}
      {file && (
        <FilePreview>
          <FilePreviewHeader>
            {/* Preview for images and videos */}
            {file.type.startsWith("image/") ? (
              <img src={URL.createObjectURL(file)} alt="Preview" />
            ) : file.type.startsWith("video/") ? (
              <video src={URL.createObjectURL(file)} controls />
            ) : (
              <span>{file.name}</span>
            )}
            <FileName>{file.name}</FileName>
          </FilePreviewHeader>

          <RemoveButton onClick={removeFile}>
            <LiaTimesSolid size={30} />
          </RemoveButton>
        </FilePreview>
      )}

      {/* Error Message */}
      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

export default FileUploader;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #ccc;
  box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.1);
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const DimensionInfo = styled.p`
  font-size: 12px;
  color: #007bff;
  font-weight: 500;
`;

const UploadBox = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border: 2px dashed #999;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;

  &:hover {
    border-color: #007bff;
  }

  p {
    font-size: 14px;
    color: #555;
  }

  span {
    color: #007bff;
    text-decoration: underline;
  }

  input {
    display: none;
  }
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background: #e0e0e0;
  border-radius: 6px;
  gap: 10px;

  img,
  video {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 6px;
  }

  span {
    font-size: 14px;
    color: #333;
  }
`;

const FilePreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FileName = styled.span`
  font-size: 14px;
  color: #333;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ff4d4d;
  cursor: pointer;

  &:hover {
    color: #d32f2f;
  }
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: red;
`;
